/**
 * PMT Announcement Bar v2 — two-stage language selector.
 *
 * The server renders the bar in its correct resting state and a pre-paint
 * script in the section applies the dismissed class. This file only adds
 * behaviour:
 *
 *   • Stage 1 pills and dropdown rows are submit buttons for Shopify's
 *     localization form, so the language switch works with JavaScript
 *     disabled. Here we only persist the choice before the navigation, and
 *     cancel the submit when it would be a no-op.
 *   • The close button — and picking a language — dismisses Stage 1.
 *   • The dropdown is a disclosure with full keyboard support.
 *
 * Persistence and analytics both live in assets/pmt-lang-store.js
 * (window.PmtLang), because the same cookie answers both "should we show the
 * prompt?" and "which language did this visitor deliberately choose?".
 * If that file is missing, persist() below still writes the cookie itself.
 *
 * Registered as a custom element so the theme editor re-upgrades it on every
 * section re-render, matching how the rest of the theme works (sticky-header,
 * localization-form).
 */

if (!customElements.get('pmt-announcement-bar')) {
  customElements.define(
    'pmt-announcement-bar',
    class PmtAnnouncementBar extends HTMLElement {
      static DISMISSED_CLASS = 'pmt-bar-v2-dismissed';
      static CURRENT_MSG_CLASS = 'pmt-bar-v2__s2-msg--current';
      /* Only used if the section somehow rendered without
         data-lang-max-age-days; the Liquid assign is the real source. */
      static DEFAULT_MAX_AGE_DAYS = 365;

      connectedCallback() {
        this.currentLocale = this.dataset.currentLocale || '';
        this.designMode = this.hasAttribute('data-design-mode');

        /* Absent entirely for signed-in customers — the section omits Stage 1
           server-side, so every path below has to tolerate a null. */
        this.stage1 = this.querySelector('[data-pmt-stage1]');
        this.dropdownWrap = this.querySelector('[data-pmt-dropdown-wrap]');
        this.dropdownBtn = this.querySelector('[data-pmt-dropdown-btn]');
        this.dropdown = this.querySelector('[data-pmt-dropdown]');
        this.rows = Array.from(this.querySelectorAll('[data-pmt-locale-select]'));

        // The theme editor must always show Stage 1 so the merchant can style
        // it, even on a browser profile that dismissed it earlier.
        if (this.designMode) {
          document.documentElement.classList.remove(PmtAnnouncementBar.DISMISSED_CLASS);
        }

        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onDocumentKeydown = this.onDocumentKeydown.bind(this);

        this.bindStage1();
        this.bindDropdown();
        this.initRotation();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKeydown);
        this.teardownRotation();
      }

      /* ── Stage 2 message rotation ──────────────────────────────────── */

      /**
       * Below 990px only one message fits, so the secondary is hidden by CSS.
       * When the merchant enables rotation we alternate them instead.
       *
       * Mobile only, deliberately: on desktop both messages are already visible
       * side by side, and cycling them there would hide information rather than
       * reveal it. The breakpoint is watched rather than read once, so rotating
       * starts and stops correctly when the window is resized or a tablet is
       * turned.
       */
      initRotation() {
        this.stage2Inner = this.querySelector('[data-pmt-stage2-inner]');
        this.msgs = Array.from(this.querySelectorAll('[data-pmt-msg]'));

        // No rotation setting, or nothing to rotate between.
        const seconds = parseFloat(this.dataset.rotateSeconds || '');
        if (!this.stage2Inner || this.msgs.length < 2) return;
        if (!isFinite(seconds) || seconds <= 0) return;

        this.rotateMs = seconds * 1000;
        this.rotatePaused = false;
        this.msgIndex = Math.max(
          0,
          this.msgs.findIndex((m) => m.classList.contains(PmtAnnouncementBar.CURRENT_MSG_CLASS))
        );

        this.mobileMq = window.matchMedia('(max-width: 989px)');
        this.syncRotation = this.syncRotation.bind(this);
        this.pauseRotation = this.pauseRotation.bind(this);
        this.resumeRotation = this.resumeRotation.bind(this);

        // Safari < 14 only has the deprecated addListener.
        if (this.mobileMq.addEventListener) {
          this.mobileMq.addEventListener('change', this.syncRotation);
        } else if (this.mobileMq.addListener) {
          this.mobileMq.addListener(this.syncRotation);
        }

        // A background tab should not burn a timer, and should not silently
        // advance several messages while nobody is looking.
        document.addEventListener('visibilitychange', this.syncRotation);

        // Pause while a keyboard user is inside the bar — otherwise the link
        // they are tabbing toward can disappear underneath them.
        this.stage2Inner.addEventListener('focusin', this.pauseRotation);
        this.stage2Inner.addEventListener('focusout', this.resumeRotation);

        // Hover-pause only on devices that genuinely hover. On touch, a tap
        // fires pointerenter with no matching pointerleave, which would pause
        // the rotation permanently after the first tap.
        if (window.matchMedia('(hover: hover)').matches) {
          this.stage2Inner.addEventListener('pointerenter', this.pauseRotation);
          this.stage2Inner.addEventListener('pointerleave', this.resumeRotation);
        }

        this.syncRotation();
      }

      teardownRotation() {
        this.stopRotationTimer();
        if (!this.mobileMq) return;

        if (this.mobileMq.removeEventListener) {
          this.mobileMq.removeEventListener('change', this.syncRotation);
        } else if (this.mobileMq.removeListener) {
          this.mobileMq.removeListener(this.syncRotation);
        }
        document.removeEventListener('visibilitychange', this.syncRotation);
        if (this.stage2Inner) {
          this.stage2Inner.removeEventListener('focusin', this.pauseRotation);
          this.stage2Inner.removeEventListener('focusout', this.resumeRotation);
          this.stage2Inner.removeEventListener('pointerenter', this.pauseRotation);
          this.stage2Inner.removeEventListener('pointerleave', this.resumeRotation);
        }
      }

      /**
       * Single place that decides whether the timer should be running. Every
       * input (breakpoint, tab visibility, hover, focus) routes through here so
       * the states cannot disagree.
       */
      syncRotation() {
        const onMobile = this.mobileMq.matches;

        // Drives the CSS: with the attribute absent, desktop shows both.
        this.stage2Inner.toggleAttribute('data-pmt-rotating', onMobile);

        if (onMobile && !this.rotatePaused && !document.hidden) {
          this.startRotationTimer();
        } else {
          this.stopRotationTimer();
        }
      }

      pauseRotation() {
        this.rotatePaused = true;
        this.syncRotation();
      }

      resumeRotation() {
        this.rotatePaused = false;
        this.syncRotation();
      }

      startRotationTimer() {
        this.stopRotationTimer();
        this.rotateTimer = setInterval(() => {
          this.showMessage((this.msgIndex + 1) % this.msgs.length);
        }, this.rotateMs);
      }

      stopRotationTimer() {
        if (!this.rotateTimer) return;
        clearInterval(this.rotateTimer);
        this.rotateTimer = null;
      }

      showMessage(index) {
        this.msgs.forEach((msg, i) => {
          msg.classList.toggle(PmtAnnouncementBar.CURRENT_MSG_CLASS, i === index);
        });
        this.msgIndex = index;
      }

      /**
       * The preference store. Returns null if pmt-lang-store.js did not
       * execute; persist() falls back to writing the cookie directly, so the
       * prompt still stops reappearing. Only the analytics event is lost.
       */
      get store() {
        return window.PmtLang || null;
      }

      /* ── Locale switching ──────────────────────────────────────────── */

      /**
       * Handles a click on a pill or a dropdown row.
       *
       * The buttons are submit buttons for the localization form, so with JS
       * off the browser navigates on its own. Returns true when a navigation
       * is going ahead.
       */
      selectLocale(isoCode, event, source) {
        const noop = isoCode === this.currentLocale || this.designMode;

        if (noop) {
          // Picking the language you are already on (or clicking in the theme
          // editor) should behave like the X: dismiss in place, no round trip.
          if (event) event.preventDefault();
          // Still a deliberate choice, so it is still worth recording — but
          // nothing is going to navigate, so the event has to fire now rather
          // than wait for a next page load that will never come.
          this.persist(isoCode, 'sel', source, { immediate: true });
          this.hideStage1({ moveFocus: true });
          return false;
        }

        // A navigation follows immediately. record() writes the preference
        // cookie synchronously and leaves a marker for the next page load to
        // report, so nothing is lost to the unload.
        this.persist(isoCode, 'sel', source);
        this.hideStage1({ moveFocus: false });
        return true;
      }

      /* ── Stage 1 ───────────────────────────────────────────────────── */

      bindStage1() {
        if (!this.stage1) return;

        this.stage1.querySelectorAll('[data-pmt-locale-pill]').forEach((btn) => {
          btn.addEventListener('click', (event) => {
            // Do not disable the button here — a disabled submitter is not
            // sent, which would drop the locale_code the form relies on.
            this.selectLocale(
              btn.dataset.pmtLocalePill,
              event,
              'announcement_bar_stage1'
            );
          });
        });

        this.stage1.querySelectorAll('[data-pmt-close]').forEach((btn) => {
          btn.addEventListener('click', () => {
            // Dismissing is itself a signal — "the language I'm on is fine" —
            // and it is the only way to tell a satisfied visitor apart from
            // one who never engaged. Recorded as state 'dis' so it can never
            // be mistaken for an explicit choice in an Intelligems audience.
            this.persist(this.currentLocale, 'dis', 'announcement_bar_dismiss', {
              immediate: true,
            });
            this.hideStage1({ moveFocus: true });
          });
        });
      }

      /**
       * Record a decision. Uses the shared store when it is available, and
       * falls back to writing the cookie here when it is not.
       *
       * The fallback matters because pmt-lang-store.js is a separate request:
       * if it 404s, throws, or is dropped by a content blocker, every dismissal
       * would otherwise be forgotten and the prompt would reappear on every
       * single page view — a UI guarantee must not depend on an optional file.
       * Analytics genuinely is optional, so the fallback writes no event.
       */
      persist(lang, state, source, opts) {
        if (this.designMode) return;
        if (this.store) {
          this.store.record(lang, state, source, opts);
          return;
        }
        this.persistFallback(lang, state);
      }

      /**
       * Last-resort write. Must produce exactly the value grammar that
       * pmt-lang-store.js writes and that the section's pre-paint script
       * validates: <schema>|<lang>|<state>|<yyyymmdd>.
       *
       * The lifetime is read from the same data-lang-max-age-days attribute the
       * store uses, so a change to the Liquid assign moves all three writers at
       * once. Hardcoding it here is what let the three drift apart before.
       */
      persistFallback(lang, state) {
        const base = String(lang || '').split('-')[0].toLowerCase();
        if (!/^[a-z]{2}$/.test(base)) return;
        if (state !== 'sel' && state !== 'dis') return;

        const d = new Date();
        const pad = (n) => (n < 10 ? '0' : '') + n;
        const stamp = '' + d.getUTCFullYear() + pad(d.getUTCMonth() + 1) + pad(d.getUTCDate());
        const value = ['1', base, state, stamp].join('|');

        const days = parseFloat(this.dataset.langMaxAgeDays);
        const maxAge = Math.round(
          (isFinite(days) && days > 0 ? days : PmtAnnouncementBar.DEFAULT_MAX_AGE_DAYS) * 86400
        );

        try {
          const bits = ['pmt_lang=' + value, 'Path=/', 'SameSite=Lax', 'Max-Age=' + maxAge];
          if (location.protocol === 'https:') bits.push('Secure');
          document.cookie = bits.join('; ');
        } catch (e) {
          /* cookies blocked — the mirror below is the fallback's fallback */
        }
        try {
          localStorage.setItem('pmt_lang', JSON.stringify({ v: value, t: Date.now() }));
        } catch (e) {}
      }

      /**
       * Hide Stage 1. Focus is moved deliberately — the close button is inside
       * the element about to be hidden, and losing focus to <body> strands
       * keyboard and screen-reader users.
       */
      hideStage1({ moveFocus }) {
        if (this.designMode) return; // keep the merchant's view intact

        const focusWasInside = this.stage1 && this.stage1.contains(document.activeElement);
        document.documentElement.classList.add(PmtAnnouncementBar.DISMISSED_CLASS);

        // Focus must move whenever it was inside the element we just hid,
        // regardless of what the caller asked for, or it falls to <body>.
        if ((focusWasInside || moveFocus) && this.dropdownBtn) this.dropdownBtn.focus();
      }

      /* ── Dropdown (disclosure) ─────────────────────────────────────── */

      bindDropdown() {
        if (!this.dropdownBtn || !this.dropdown) return;

        this.dropdownBtn.addEventListener('click', (event) => {
          event.stopPropagation();
          if (this.isOpen()) {
            this.closeDropdown();
            return;
          }
          // Move focus into the list only for keyboard activation, so a mouse
          // click does not make the focus ring jump. Two signals because
          // neither is universal: detail === 0 for a synthesised Enter/Space
          // click, :focus-visible for the browser's own heuristic.
          const keyboard = event.detail === 0 || this.dropdownBtn.matches(':focus-visible');
          this.openDropdown({ moveFocus: keyboard });
        });

        this.rows.forEach((row, index) => {
          row.addEventListener('click', (event) => {
            if (
              !this.selectLocale(
                row.dataset.pmtLocaleSelect,
                event,
                'announcement_bar_stage2'
              )
            ) {
              this.closeDropdown({ restoreFocus: true });
            }
          });

          row.addEventListener('keydown', (event) => this.onRowKeydown(event, index));
        });

        // Tabbing (or clicking) out of the panel closes it. Doing this on
        // focusout rather than on the Tab key lets the browser finish moving
        // focus before the panel is hidden.
        this.dropdownWrap.addEventListener('focusout', (event) => {
          if (!this.dropdownWrap.contains(event.relatedTarget)) this.closeDropdown();
        });

        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onDocumentKeydown);
      }

      isOpen() {
        return this.dropdownWrap.hasAttribute('data-open');
      }

      openDropdown({ moveFocus = false } = {}) {
        // `hidden` rather than opacity alone: a panel hidden only by opacity
        // leaves its buttons in the tab order on every page of the store, so a
        // keyboard user can silently switch the site language by tabbing into
        // something they cannot see.
        this.dropdown.hidden = false;
        // Force a reflow so the opacity/scale transition actually runs —
        // without it the element goes from display:none straight to open.
        void this.dropdown.offsetWidth;

        this.dropdownWrap.setAttribute('data-open', '');
        this.dropdownBtn.setAttribute('aria-expanded', 'true');

        if (!moveFocus) return;
        const active = this.rows.find((row) => row.hasAttribute('aria-current')) || this.rows[0];
        if (active) active.focus();
      }

      closeDropdown({ restoreFocus = false } = {}) {
        if (!this.isOpen()) return;

        this.dropdownWrap.removeAttribute('data-open');
        this.dropdownBtn.setAttribute('aria-expanded', 'false');
        this.dropdown.hidden = true;

        if (restoreFocus) this.dropdownBtn.focus();
      }

      onRowKeydown(event, index) {
        const last = this.rows.length - 1;
        let next = null;

        switch (event.key) {
          case 'ArrowDown':
            next = index === last ? 0 : index + 1;
            break;
          case 'ArrowUp':
            next = index === 0 ? last : index - 1;
            break;
          case 'Home':
            next = 0;
            break;
          case 'End':
            next = last;
            break;
          default:
            return;
        }

        event.preventDefault();
        this.rows[next].focus();
      }

      onDocumentClick(event) {
        if (this.dropdownWrap && !this.dropdownWrap.contains(event.target)) {
          this.closeDropdown();
        }
      }

      onDocumentKeydown(event) {
        if (event.key !== 'Escape' || !this.isOpen()) return;
        this.closeDropdown({ restoreFocus: true });
      }
    }
  );
}
